import React from "react";
import styled, { keyframes } from "styled-components";
import { Pause, Play, Target, Coffee, TimerReset, ArrowUpRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatFocusTime, useFocusTimer } from "../../context/FocusTimerContext";

const FloatingShell = styled.button`
  position: fixed;
  left: 18px;
  bottom: 18px;
  z-index: 1400;
  width: min(340px, calc(100vw - 32px));
  padding: 14px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(10, 18, 34, 0.96), rgba(3, 7, 18, 0.94)),
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.18), transparent 38%);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.42);
  color: #f8fafc;
  text-align: left;
  cursor: pointer;
  backdrop-filter: blur(18px);

  @media (max-width: 640px) {
    left: 12px;
    bottom: 12px;
    width: min(100%, calc(100vw - 24px));
    padding: 12px;
    border-radius: 20px;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const StageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const IconTile = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $tone }) => $tone};
  background: ${({ $soft }) => $soft};
  border: 1px solid ${({ $tone }) => `${$tone}33`};
  flex-shrink: 0;
`;

const LabelWrap = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.div`
  color: #7dd3fc;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Label = styled.div`
  margin-top: 4px;
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const tickReveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-0.82em);
    filter: blur(3px);
    text-shadow: 0 0 0 rgba(125, 211, 252, 0);
  }

  55% {
    opacity: 0.74;
    transform: translateY(0.06em);
    filter: blur(0.5px);
    text-shadow: 0 0 12px rgba(125, 211, 252, 0.12);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
    text-shadow: 0 0 0 rgba(125, 211, 252, 0);
  }
`;

const TimePill = styled.div`
  padding: 9px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #f8fafc;
  font-size: 18px;
  font-weight: 1000;
  letter-spacing: -0.04em;
  flex-shrink: 0;
  animation: ${tickReveal} 280ms ease;
`;

const TimeDigits = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.01em;
`;

const TimeChar = styled.span`
  display: inline-block;
  min-width: ${({ $isDivider }) => ($isDivider ? "0.28em" : "0.54em")};
  will-change: transform, opacity, filter;
  animation: ${({ $changed }) => ($changed ? tickReveal : "none")} 340ms cubic-bezier(0.22, 1, 0.36, 1);
  animation-delay: ${({ $changed, $index }) => ($changed ? `${$index * 40}ms` : "0ms")};
  animation-fill-mode: ${({ $changed }) => ($changed ? "both" : "none")};
`;

const ProgressTrack = styled.div`
  margin-top: 12px;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
`;

const ProgressFill = styled.div`
  width: ${({ $percent }) => `${$percent}%`};
  height: 100%;
  border-radius: 999px;
  background: ${({ $gradient }) => $gradient};
  transition: width 1s linear;
`;

const BottomRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const Hint = styled.div`
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.5;
  max-width: 190px;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const MiniButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.72);
  color: #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const OpenLink = styled.div`
  padding: 0 12px;
  min-height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.18), rgba(56, 189, 248, 0.18));
  border: 1px solid rgba(245, 158, 11, 0.18);
  color: #f8fafc;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
`;

export default function FocusTimerFAB() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    state,
    stageMeta,
    progressPercent,
    showFloatingTimer,
    toggleTimer,
    resetCurrentStage,
  } = useFocusTimer();

  const timeText = formatFocusTime(state.remainingSeconds);
  const previousTimeRef = React.useRef(timeText);
  const previousTimeText = previousTimeRef.current;

  React.useEffect(() => {
    previousTimeRef.current = timeText;
  }, [timeText]);

  if (!showFloatingTimer || location.pathname === "/focus-without-phone") {
    return null;
  }

  const StageIcon = state.stage === "work" ? Target : Coffee;

  return (
    <FloatingShell onClick={() => navigate("/focus-without-phone")} aria-label="Open focus without phone timer">
      <TopRow>
        <StageMeta>
          <IconTile $tone={stageMeta.tone} $soft={stageMeta.soft}>
            <StageIcon size={18} />
          </IconTile>
          <LabelWrap>
            <Eyebrow>Focus without phone</Eyebrow>
            <Label>{stageMeta.label}</Label>
          </LabelWrap>
        </StageMeta>
        <TimePill>
          <TimeDigits>
            {timeText.split("").map((char, index) => (
              <TimeChar
                key={`${index}-${char === ":" ? "divider" : "digit"}`}
                $index={index}
                $isDivider={char === ":"}
                $changed={previousTimeText[index] !== char}
              >
                {char}
              </TimeChar>
            ))}
          </TimeDigits>
        </TimePill>
      </TopRow>

      <ProgressTrack>
        <ProgressFill $percent={progressPercent} $gradient={stageMeta.gradient} />
      </ProgressTrack>

      <BottomRow>
        <Hint>
          {state.isRunning
            ? "Timer is running. Keep the current block alive across pages."
            : "Your current focus block is saved. Resume anytime."}
        </Hint>

        <ActionRow onClick={(event) => event.stopPropagation()}>
          <MiniButton onClick={toggleTimer} aria-label={state.isRunning ? "Pause timer" : "Start timer"}>
            {state.isRunning ? <Pause size={16} /> : <Play size={16} />}
          </MiniButton>
          <MiniButton onClick={resetCurrentStage} aria-label="Reset timer">
            <TimerReset size={16} />
          </MiniButton>
          <OpenLink onClick={() => navigate("/focus-without-phone")}>
            Open
            <ArrowUpRight size={14} />
          </OpenLink>
        </ActionRow>
      </BottomRow>
    </FloatingShell>
  );
}
